import React from "react";
import { Outlet, useNavigate } from "react-router-dom";

import {
  HomeOutlined,
  MailOutlined,
  UserOutlined,
  SettingOutlined,
  FormOutlined,
  FileTextOutlined,
  ThunderboltOutlined,
  SafetyCertificateOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
} from "@ant-design/icons";
import { Drawer, Grid, Layout, Menu } from "antd";

import { useEffect, useState } from "react";
import ReactivateSession from "../../components/modals/reactivateSession";
import AppHeader from "../../components/AppHeader";
import NoOrganizationGate from "../../components/NoOrganizationGate";
import { useTheme } from "../../theme/ThemeContext";
import { useThemedLogo, useThemedLogoMark } from "../../theme/useThemedLogo";

const { Header, Content, Sider } = Layout;

const items = [
  {
    label: "Home",
    key: "",
    icon: <HomeOutlined />,
    disabled: false,
  },
  {
    label: "Campaigns",
    key: "campaigns",
    icon: <MailOutlined />,
    disabled: false,
  },
  {
    label: "Contacts",
    key: "contacts",
    icon: <UserOutlined />,
    disabled: false,
  },
  {
    label: "Forms",
    key: "forms",
    icon: <FormOutlined />,
    disabled: false,
  },
  {
    label: "Sequences",
    key: "sequences",
    icon: <ThunderboltOutlined />,
    disabled: false,
  },
  {
    label: "Templates",
    key: "templates",
    icon: <FileTextOutlined />,
    disabled: false,
  },
  {
    label: "Compliance",
    key: "compliance",
    icon: <SafetyCertificateOutlined />,
    disabled: false,
  },
  {
    label: "Settings",
    key: "settings",
    icon: <SettingOutlined />,
    disabled: false,
  },
];

const SIDEBAR_STORAGE_KEY = "app-sidebar-collapsed";

function SidebarNav({
  collapsed,
  isDark,
  logo,
  logoMark,
  current,
  onClick,
  onToggleCollapse,
  showCollapseButton = true,
}) {
  return (
    <div className="app-sidebar-inner">
      <div className="app-sidebar-brand">
        <a href="/app" aria-label="Mailinflow home">
          <img
            src={collapsed ? logoMark : logo}
            alt="Mailinflow"
            className={`app-sidebar-logo-img${collapsed ? " app-sidebar-logo-img--mark" : ""}`}
          />
        </a>
      </div>
      <nav className="app-sidebar-nav" aria-label="Main navigation">
        <Menu
          className="app-sidebar-menu"
          theme={isDark ? "dark" : "light"}
          onClick={onClick}
          selectedKeys={[current]}
          mode="inline"
          inlineCollapsed={collapsed}
          items={items}
        />
      </nav>
      {showCollapseButton ? (
        <div className="app-sidebar-footer">
          <button
            type="button"
            className="app-sidebar-collapse-btn"
            onClick={onToggleCollapse}
            aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            aria-expanded={!collapsed}
            title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            {collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
            {!collapsed ? <span>Collapse</span> : null}
          </button>
        </div>
      ) : null}
    </div>
  );
}

const { useBreakpoint } = Grid;

export default function App() {
  const [current, setCurrent] = useState("");
  const [sessionExpired, setSessionExpired] = useState(false);
  const [collapsed, setCollapsed] = useState(
    () => localStorage.getItem(SIDEBAR_STORAGE_KEY) === "true",
  );
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const logo = useThemedLogo();
  const logoMark = useThemedLogoMark();
  const screens = useBreakpoint();
  const isMobile = screens.md === false;

  const onClick = (e) => {
    setCurrent(e.key);
    navigate(e.key);
    setMobileNavOpen(false);
  };

  const clearSession = () => {
    localStorage.clear();
    setSessionExpired(false);
    navigate("/");
    setTimeout(() => {
      window.location.reload();
    }, 1000);
  };

  /**
   * Session Management Effect
   *
   * This effect handles two main responsibilities:
   * 1. Navigation state management - Updates the current path based on URL
   * 2. Session expiration monitoring - Checks if user session has expired
   *    - Immediately checks expiration on mount/path change
   *    - Sets up interval to check every minute if session is still valid
   *    - Triggers session expired state when time is up
   */
  useEffect(() => {
    const path = window.location.pathname.split("/").pop();
    setCurrent(path === "app" ? "" : path);
    const now = new Date().getTime();
    const expireAt = parseInt(localStorage.getItem("expireAt"));
    if (now > expireAt) {
      setSessionExpired(true);
    } else {
      const timer = setInterval(() => {
        const currentNow = new Date().getTime();
        if (currentNow > expireAt) {
          clearInterval(timer);
          setSessionExpired(true);
        }
      }, 60000);
    }
  }, []);

  // Only persist a manual toggle, not the auto-collapse antd applies via the
  // Sider's `breakpoint` prop when crossing into mobile widths — otherwise
  // resizing/rotating near the breakpoint would silently overwrite (and
  // eventually always force-collapse) the user's saved desktop preference.
  const toggleSidebar = () => {
    setCollapsed((value) => {
      const next = !value;
      localStorage.setItem(SIDEBAR_STORAGE_KEY, next ? "true" : "false");
      return next;
    });
  };

  return (
    <>
      <ReactivateSession
        sessionExpired={sessionExpired}
        setSessionExpired={setSessionExpired}
        clearSession={clearSession}
      />
      <Layout className="app-layout" hasSider>
        {isMobile ? (
          <Drawer
            placement="left"
            width={240}
            open={mobileNavOpen}
            onClose={() => setMobileNavOpen(false)}
            closable={false}
            rootClassName="app-sidebar-drawer"
            styles={{ body: { padding: 0 } }}
          >
            <SidebarNav
              collapsed={false}
              isDark={isDark}
              logo={logo}
              logoMark={logoMark}
              current={current}
              onClick={onClick}
              showCollapseButton={false}
            />
          </Drawer>
        ) : (
          <Sider
            className={`app-sidebar${collapsed ? " is-collapsed" : ""}`}
            collapsible
            collapsed={collapsed}
            onCollapse={setCollapsed}
            trigger={null}
            width={240}
            collapsedWidth={72}
            theme={isDark ? "dark" : "light"}
          >
            <SidebarNav
              collapsed={collapsed}
              isDark={isDark}
              logo={logo}
              logoMark={logoMark}
              current={current}
              onClick={onClick}
              onToggleCollapse={toggleSidebar}
            />
          </Sider>
        )}
        <Layout>
          <Header className="app-layout-header">
            <AppHeader
              onLogout={clearSession}
              isMobile={isMobile}
              onOpenNav={() => setMobileNavOpen(true)}
            />
          </Header>
          <Content>
            <NoOrganizationGate>
              <Outlet />
            </NoOrganizationGate>
          </Content>
        </Layout>
      </Layout>
    </>
  );
}
